import { useQuery } from "@tanstack/react-query";
import type {
  ProductAvailabilitySummaryView,
  ProductContentEnrichmentStateView,
  ProductContentSection,
  ProductPricingSummaryView,
  ProductSellerHiddenReason,
  ProductVariantView,
  SellerProductDetailView,
} from "@/lib/api/types";
import { fetchSellerProductById } from "./seller-product-detail";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseProductByIdQueryParams = {
  storeSlug: string;
  productId: string;
};

const CONTENT_SECTIONS: ProductContentSection[] = [
  "short_description",
  "highlights",
  "details",
  "faq",
  "notes",
];

const SELLER_HIDDEN_REASONS: ProductSellerHiddenReason[] = [
  "product_inactive",
  "missing_images",
  "no_active_purchasable_variants",
];

function isPurchasableVariant(
  variant: Pick<
    ProductVariantView,
    "isActive" | "forceOutOfStock" | "stockCount" | "unlimitedStock"
  >,
): boolean {
  // Unlimited-stock variants are always purchasable regardless of stockCount.
  // Mirrors the backend (product-mapper.helper) and cardAttention.ts — omitting
  // it here wrongly flagged unlimited, stock-0 products as "out of stock".
  return (
    variant.isActive &&
    !variant.forceOutOfStock &&
    (variant.unlimitedStock || variant.stockCount > 0)
  );
}

function toMoney(value: number): string {
  return value.toFixed(2);
}

function deriveDefaultVariantId(
  product: SellerProductDetailView,
  variants: ProductVariantView[],
): string {
  const fallbackId = product.defaultVariantId || variants[0]?.id || "";
  if (!fallbackId) {
    return "";
  }
  const hasKnownDefault = variants.some((variant) => variant.id === fallbackId);
  return hasKnownDefault ? fallbackId : variants[0]?.id || "";
}

function derivePricingSummary(
  product: SellerProductDetailView,
  variants: ProductVariantView[],
): ProductPricingSummaryView {
  const providedSummary = product.pricingSummary;
  if (
    providedSummary &&
    typeof providedSummary.currentMin === "string" &&
    providedSummary.currentMin.length > 0
  ) {
    return providedSummary;
  }

  if (variants.length === 0) {
    return {
      mode: "single",
      currentMin: toMoney(0),
      currentMax: null,
      originalMin: null,
      originalMax: null,
    };
  }

  const purchasableVariants = variants.filter(isPurchasableVariant);
  const activeNotForcedVariants = variants.filter(
    (variant) => variant.isActive && !variant.forceOutOfStock,
  );
  const candidates =
    purchasableVariants.length > 0
      ? purchasableVariants
      : activeNotForcedVariants.length > 0
        ? activeNotForcedVariants
        : variants;

  const currentPrices = candidates.map(
    (variant) => variant.discountedPrice ?? variant.price,
  );
  const originalPrices = candidates.map((variant) => variant.price);
  const currentMin = Math.min(...currentPrices);
  const currentMax = Math.max(...currentPrices);
  const originalMin = Math.min(...originalPrices);
  const originalMax = Math.max(...originalPrices);
  const isSingle = currentMin === currentMax;

  return {
    mode: isSingle ? "single" : "range",
    currentMin: toMoney(currentMin),
    currentMax: isSingle ? null : toMoney(currentMax),
    originalMin: toMoney(originalMin),
    originalMax: isSingle ? null : toMoney(originalMax),
  };
}

function deriveAvailabilitySummary(
  product: SellerProductDetailView,
  variants: ProductVariantView[],
  defaultVariantId: string,
): ProductAvailabilitySummaryView {
  if (variants.length === 0) {
    const providedSummary = product.availabilitySummary;
    if (providedSummary) {
      return {
        hasPurchasableVariant: Boolean(providedSummary.hasPurchasableVariant),
        availableVariantCount: Number(providedSummary.availableVariantCount ?? 0),
        defaultVariantId: providedSummary.defaultVariantId || defaultVariantId,
      };
    }
    return {
      hasPurchasableVariant: false,
      availableVariantCount: 0,
      defaultVariantId,
    };
  }

  const availableVariantCount = variants.filter(isPurchasableVariant).length;
  return {
    hasPurchasableVariant: availableVariantCount > 0,
    availableVariantCount,
    defaultVariantId,
  };
}

function deriveSellerHiddenReasons(
  product: SellerProductDetailView,
  availabilitySummary: ProductAvailabilitySummaryView,
): ProductSellerHiddenReason[] {
  const existingReasons = (product.sellerBuyerHiddenReasons ?? []).filter(
    (reason): reason is ProductSellerHiddenReason =>
      SELLER_HIDDEN_REASONS.includes(reason),
  );
  if (existingReasons.length > 0) {
    return [...new Set(existingReasons)];
  }

  const reasons: ProductSellerHiddenReason[] = [];
  if (!product.isActive) {
    reasons.push("product_inactive");
  }
  if ((product.images?.length ?? 0) === 0) {
    reasons.push("missing_images");
  }
  if (!availabilitySummary.hasPurchasableVariant) {
    reasons.push("no_active_purchasable_variants");
  }
  return reasons;
}

function hasText(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function deriveContentEnrichmentState(
  product: SellerProductDetailView,
): ProductContentEnrichmentStateView {
  const completed = new Set<ProductContentSection>();

  if (hasText(product.shortDescriptionAr)) {
    completed.add("short_description");
  }
  if (Array.isArray(product.highlights) && product.highlights.length > 0) {
    completed.add("highlights");
  }
  if (Array.isArray(product.details) && product.details.length > 0) {
    completed.add("details");
  }
  if (Array.isArray(product.faq) && product.faq.length > 0) {
    completed.add("faq");
  }
  if (Array.isArray(product.notes) && product.notes.length > 0) {
    completed.add("notes");
  }

  const missingSections = CONTENT_SECTIONS.filter((section) => !completed.has(section));
  const totalSections = CONTENT_SECTIONS.length;
  const completedSections = totalSections - missingSections.length;

  return {
    completedSections,
    totalSections,
    missingSections,
    isEnriched: missingSections.length === 0,
  };
}

export function normalizeSellerProductDetail(
  product: SellerProductDetailView,
): SellerProductDetailView {
  const variants = product.variants ?? [];
  const defaultVariantId = deriveDefaultVariantId(product, variants);
  const normalizedVariants = variants.map((variant) => ({
    ...variant,
    isDefault: variant.id === defaultVariantId,
  }));
  const pricingSummary = derivePricingSummary(product, normalizedVariants);
  const availabilitySummary = deriveAvailabilitySummary(
    product,
    normalizedVariants,
    defaultVariantId,
  );

  return {
    ...product,
    defaultVariantId,
    variants: normalizedVariants,
    pricingSummary,
    availabilitySummary,
    sellerBuyerHiddenReasons: deriveSellerHiddenReasons(product, availabilitySummary),
    contentEnrichmentState: deriveContentEnrichmentState(product),
  };
}

export function useProductByIdQuery({
  storeSlug,
  productId,
}: UseProductByIdQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.productById(storeSlug, productId),
    queryFn: () => fetchSellerProductById(productId),
    enabled: storeSlug.length > 0 && productId.length > 0,
  });

  return {
    data: query.data ? normalizeSellerProductDetail(query.data) : undefined,
    isLoading: query.isLoading,
    error: query.error,
  };
}
